import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import GlobalLoader from './shared/ui/GlobalLoader';
import { LayoutDashboard, Users, Database, BarChart3, Settings, LogOut, Plus, Eye, Edit, Trash2, Play, Menu, X } from 'lucide-react';
import LoginView from './domains/auth/pages/login';
import LandingPage from './domains/auth/pages/LandingPage';
import DashboardsView from './domains/dashboards/pages/DashboardsView';
import UsersView from './domains/users/pages/UsersView';
import Sidebar from './shared/ui/Sidebar';
import DataSourcesView from './domains/datasources/pages/DataSourcesView';
import CompaniesView from './domains/companies/pages/CompaniesView';
import AdminOverview from './domains/admin/pages/AdminOverview';
import ClientDashboard from './domains/admin/pages/ClientDashboard';
import ActivityView from './domains/admin/pages/ActivityView';
import { INTERNAL_COMPANY } from './config/constants';
import DataModelView from './domains/datasources/pages/DataModelView';
import DataModeling from './domains/datasources/pages/DataModeling';
import ChatAnalitico from './domains/chat-analytics/pages/ChatAnalitico';
import CredentialsPage from './domains/auth/pages/CredentialsPage';
import AionAssistant from './shared/ui/AionAssistant';
import OnboardingEmpresa from './domains/auth/pages/OnboardingEmpresa';
import AuthCallback from './domains/auth/components/AuthCallback';
import DashboardPreview from './domains/dashboards/pages/DashboardPreview'; // 🧪 Preview for new executive system
import ExecutivePreview from './domains/executive/pages/ExecutivePreview'; // 🎯 Executive system preview
import DashboardPage from './pages/DashboardPage'; // 📊 Dashboard page for all dashboards
import { showDevWarnings } from './utils/userValidator';
import { initStorage } from './utils/storage';
import { isRoot } from './utils/permissions';
import { queryKeys } from './shared/services/queryKeys';
import { useUIStore } from './shared/state/uiStore';

// API
import { getUsers as apiGetUsers, createUser as apiCreateUser, updateUser as apiUpdateUser, deleteUser as apiDeleteUser } from './domains/users/services/users.service';
import { getDashboards as apiGetDashboards, getDashboard as apiGetDashboard, updateDashboard as apiUpdateDashboard, deleteDashboard as apiDeleteDashboard } from './domains/dashboards/services/dashboards.service';
import { getDataSources as apiGetDataSources, createDataSource as apiCreateDataSource, updateDataSource as apiUpdateDataSource, deleteDataSource as apiDeleteDataSource } from './domains/datasources/services/datasources.service';
import { logActivity } from './shared/services/activityLogger';
import { getCompanies as apiGetCompanies, createCompany as apiCreateCompany, updateCompany as apiUpdateCompany, deleteCompany as apiDeleteCompany } from './domains/companies/services/companies.service';
import { login as apiLogin } from './domains/auth/services/auth.service';

// NO MOCK DATA - Always load from API to avoid DB inconsistencies


const App = () => {
  // Initialize storage on app start (clears stale data if needed)
  React.useEffect(() => {
    initStorage();
  }, []);

  const [currentView, setCurrentView] = useState(() => {
    // Restaurar vista desde localStorage solo en F5
    const savedView = localStorage.getItem('lastView');
    const hasUser = localStorage.getItem('currentUser');
    return (savedView && hasUser) ? savedView : 'landing';
  });
  
  // Guardar vista actual en localStorage (solo para F5)
  React.useEffect(() => {
    if (currentView !== 'landing' && currentView !== 'login') {
      localStorage.setItem('lastView', currentView);
    }
  }, [currentView]);
  
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number | undefined>(undefined);
  const [selectedDashboard, setSelectedDashboard] = useState<any>(() => {
    // Restaurar dashboard seleccionado en F5
    const saved = localStorage.getItem('selectedDashboard');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Guardar dashboard seleccionado para F5
  React.useEffect(() => {
    if (selectedDashboard) {
      localStorage.setItem('selectedDashboard', JSON.stringify(selectedDashboard));
    } else {
      localStorage.removeItem('selectedDashboard');
    }
  }, [selectedDashboard]);
  
  const [selectedDatasourceId, setSelectedDatasourceId] = useState<string | number | undefined>(undefined);
  const [credentials, setCredentials] = useState({ email: '', password: '', domain: '' });
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalLoadingText, setGlobalLoadingText] = useState('Procesando...');
  const queryClient = useQueryClient();
  const canFetchProtected = Boolean(currentUser && localStorage.getItem('token'));

  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: apiGetUsers,
    enabled: canFetchProtected,
  });

  const dashboardsQuery = useQuery({
    queryKey: queryKeys.dashboards,
    queryFn: apiGetDashboards,
    enabled: canFetchProtected,
  });

  const {
    isLoading: dashboardsLoading,
    isFetching: dashboardsFetching,
    isError: dashboardsError,
    refetch: refetchDashboards,
  } = dashboardsQuery;

  const datasourcesQuery = useQuery({
    queryKey: queryKeys.datasources,
    queryFn: apiGetDataSources,
    enabled: canFetchProtected,
  });

  const {
    isLoading: datasourcesLoading,
    isFetching: datasourcesFetching,
    isError: datasourcesError,
    refetch: refetchDatasources,
  } = datasourcesQuery;

  const companiesQuery = useQuery({
    queryKey: queryKeys.companies,
    queryFn: apiGetCompanies,
    enabled: canFetchProtected,
  });

  const users = usersQuery.data ?? [];
  const dashboards = dashboardsQuery.data ?? [];
  const dataSources = datasourcesQuery.data ?? [];
  const companies = companiesQuery.data ?? [];

  useEffect(() => {
    const handleNavigate = async (event: Event) => {
      const detail = (event as CustomEvent).detail as { view?: string; dashboardId?: string } | undefined;
      if (!detail?.view) return;
      const dashboardId = detail.dashboardId;
      if (dashboardId) {
        try {
          const dashboard = await queryClient.fetchQuery({
            queryKey: queryKeys.dashboard(dashboardId),
            queryFn: () => apiGetDashboard(dashboardId),
          });
          if (dashboard) {
            setSelectedDashboard(dashboard);
          }
        } catch (error) {
          console.error('Error loading dashboard for navigation', error);
        }
      }
      setCurrentView(detail.view);
    };

    window.addEventListener('aion:navigate', handleNavigate as EventListener);
    return () => window.removeEventListener('aion:navigate', handleNavigate as EventListener);
  }, [queryClient]);

  useEffect(() => {
    if (!canFetchProtected) return;

    if (currentView === 'dashboards') {
      void refetchDashboards();
    }

    if (currentView === 'datasources') {
      void refetchDatasources();
    }
  }, [canFetchProtected, currentView, refetchDashboards, refetchDatasources]);

  const createUserMutation = useMutation({
    mutationFn: apiCreateUser,
    onSuccess: (created) => {
      queryClient.setQueryData(queryKeys.users, (prev: any[] = []) => [created, ...prev]);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
      apiUpdateUser(id, payload),
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData(queryKeys.users, (prev: any[] = []) =>
        prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: apiDeleteUser,
    onSuccess: (_, id) => {
      queryClient.setQueryData(queryKeys.users, (prev: any[] = []) => prev.filter((item) => item.id !== id));
    },
  });

  const updateDashboardMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      apiUpdateDashboard(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.dashboards, (prev: any[] = []) =>
        prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
      queryClient.setQueryData(queryKeys.dashboard(updated.id), updated);
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: apiDeleteDashboard,
    onSuccess: (_, id) => {
      queryClient.setQueryData(queryKeys.dashboards, (prev: any[] = []) => prev.filter((item) => item.id !== id));
    },
  });

  const createDataSourceMutation = useMutation({
    mutationFn: apiCreateDataSource,
    onSuccess: (created) => {
      queryClient.setQueryData(queryKeys.datasources, (prev: any[] = []) => [created, ...prev]);
    },
  });

  const updateDataSourceMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
      apiUpdateDataSource(id, payload),
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData(queryKeys.datasources, (prev: any[] = []) =>
        prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
    },
  });

  const deleteDataSourceMutation = useMutation({
    mutationFn: apiDeleteDataSource,
    onSuccess: (_, id) => {
      queryClient.setQueryData(queryKeys.datasources, (prev: any[] = []) =>
        prev.filter((item) => item.id !== id)
      );
    },
  });

  const createCompanyMutation = useMutation({
    mutationFn: apiCreateCompany,
    onSuccess: (created) => {
      queryClient.setQueryData(queryKeys.companies, (prev: any[] = []) => [created, ...prev]);
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
      apiUpdateCompany(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.companies, (prev: any[] = []) =>
        prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
    },
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: apiDeleteCompany,
    onSuccess: (_, id) => {
      queryClient.setQueryData(queryKeys.companies, (prev: any[] = []) =>
        prev.filter((item) => item.id !== id)
      );
    },
  });

  // Detectar si estamos en la ruta de callback de OAuth o executive-preview
  React.useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setCurrentView('auth-callback');
    } else if (window.location.pathname === '/executive-preview') {
      setCurrentView('executive-preview');
    }
  }, []);

  const handleLogin = async () => {
    try {
      const resp = await apiLogin({ email: credentials.email, password: credentials.password, domain: credentials.domain });
      if (resp) {
        // Validate user in development
        showDevWarnings(resp);
        
        setCurrentUser(resp);
        
        // Check if EXTERNAL user without empresaId - redirect to onboarding
        const userType = (resp as any).userType || (resp as any).type;
        const empresaId = (resp as any).empresaId || (resp as any).companyId;
        
        // ✅ Guardar empresaId en localStorage para uso en componentes
        if (empresaId) {
          localStorage.setItem('empresaId', empresaId);
          console.log('✅ empresaId saved to localStorage:', empresaId);
        }
        
        if (userType === 'EXTERNAL' && !empresaId) {
          console.log('🔄 EXTERNAL user without empresaId, redirecting to onboarding');
          setCurrentView('onboarding');
          return;
        }
        
        // If external user with empresaId, auto-select their company
        if (userType === 'EXTERNAL' && empresaId) {
          setSelectedCompanyId(empresaId);
        }
        
        setCurrentView('dashboards');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al iniciar sesión';
      toast.error(msg);
    }
  };

  // restore session from localStorage if present
  React.useEffect(() => {
    // Don't override special routes
    if (window.location.pathname === '/executive-preview' || window.location.pathname === '/auth/callback') {
      return;
    }
    
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        const u = JSON.parse(raw);
        
        // Validate user in development
        showDevWarnings(u);
        
        setCurrentUser(u);
        
        // Check if EXTERNAL user without empresaId - redirect to onboarding
        const userType = u.userType || u.type;
        const empresaId = u.empresaId || u.companyId;
        
        // ✅ Restaurar empresaId en localStorage si no está
        if (empresaId && !localStorage.getItem('empresaId')) {
          localStorage.setItem('empresaId', empresaId);
          console.log('✅ empresaId restored to localStorage:', empresaId);
        }
        
        if (userType === 'EXTERNAL' && !empresaId) {
          console.log('🔄 EXTERNAL user without empresaId, redirecting to onboarding');
          setCurrentView('onboarding');
          return;
        }
        
        // If external user with empresaId, auto-select their company
        if (userType === 'EXTERNAL' && empresaId) {
          setSelectedCompanyId(empresaId);
        }
        
        // ✅ NO cambiar la vista aquí - mantener la actual en F5
        // Solo después del login se cambia a dashboards
      }
    } catch (e) {}
  }, []);


  // DashboardViewer moved to a dedicated view: ./views/DashboardViewer

  // Settings View
  const SettingsView = () => (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-white mb-2">Configuración</h1>
      <p className="text-slate-400 mb-8">Configura los parámetros del sistema</p>
      
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-semibold text-white mb-4">Configuración General</h3>
        <p className="text-slate-400">Próximamente disponible en el MVP...</p>
      </div>
    </div>
  );

  // Landing page (no layout)
  if (currentView === 'landing') {
    return (
      <>
        <Toaster position="bottom-right" />
        <LandingPage onLogin={() => setCurrentView('login')} />
      </>
    );
  }

  // Login page (no layout)
  if (currentView === 'login') {
    return (
      <>
        <Toaster position="bottom-right" />
        <LoginView 
          credentials={credentials} 
          setCredentials={setCredentials} 
          handleLogin={handleLogin}
          onBack={() => setCurrentView('landing')}
        />
      </>
    );
  }

  // Google OAuth Callback (no layout)
  if (currentView === 'auth-callback') {
    return (
      <>
        <Toaster position="bottom-right" />
        <AuthCallback
          onSuccess={(token) => {
            // Token ya está guardado en localStorage por AuthCallback
            // Cargar usuario desde localStorage
            const raw = localStorage.getItem('currentUser');
            if (raw) {
              const user = JSON.parse(raw);
              setCurrentUser(user);
              
              // Verificar si necesita onboarding
              const userType = user.userType || user.type;
              const empresaId = user.empresaId;
              
              if (userType === 'EXTERNAL' && !empresaId) {
                setCurrentView('onboarding');
              } else {
                if (userType === 'EXTERNAL' && empresaId) {
                  setSelectedCompanyId(empresaId);
                }
                setCurrentView('dashboards');
              }
            }
          }}
          onError={(error) => {
            toast.error(error);
            setCurrentView('login');
          }}
        />
      </>
    );
  }

  // Onboarding page (no layout) - for EXTERNAL users without empresaId
  if (currentView === 'onboarding') {
    return (
      <>
        <Toaster position="bottom-right" />
        <OnboardingEmpresa 
          currentUser={currentUser}
          onComplete={(updatedUser) => {
            // ✅ CRITICAL: Update user state and JWT token
            console.log('🎉 [App] Onboarding completed, updating state and token:', updatedUser);
            setCurrentUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            setSelectedCompanyId(updatedUser.empresaId);
            
            // ✅ CRITICAL: Axios will automatically use the new token from localStorage
            // The interceptor in axios.config.ts reads from localStorage on each request
            
            // ✅ Reload companies to include the new one
            queryClient.invalidateQueries({ queryKey: queryKeys.companies })
              .then(() => {
                console.log('✅ [App] Companies reloaded after onboarding');
              })
              .catch(err => {
                console.error('❌ [App] Error reloading companies:', err);
              });
            
            // Navigate to dashboards
            setCurrentView('dashboards');
          }}
          onLogout={() => {
            localStorage.clear();
            setCurrentUser(null);
            setCurrentView('login');
          }}
        />
      </>
    );
  }

  // Dashboard app (with sidebar and layout)
  return (
    <div className="flex min-h-screen bg-slate-950">
      {/* Toaster for notifications (global) */}
      <Toaster position="bottom-right" />
      {/* Global loader (toggle via state when needed) */}
      <GlobalLoader visible={false} />
      
      {/* Botón volver - GLOBAL - Solo visible en viewer */}
      {currentView === 'viewer' && selectedDashboard && (
        <button
          onClick={() => setCurrentView('dashboards')}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 10000,
            width: '40px',
            height: '40px',
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid #334155',
            borderRadius: '8px',
            color: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'auto'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(51, 65, 85, 0.95)';
            e.currentTarget.style.borderColor = '#475569';
            e.currentTarget.style.transform = 'translateX(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(30, 41, 59, 0.95)';
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          title="Volver"
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
      )}
      
      <Sidebar
        currentUser={currentUser}
        currentView={currentView}
        setCurrentView={setCurrentView}
        setCurrentUser={setCurrentUser}
      />
      <div className={`flex-1 flex flex-col transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-4' : 'translate-x-0'}`}>
        {/* Header sticky - SIMPLE CSS FIX */}
        {currentView !== 'chat-analitico' && (
          <div className="sticky top-0 z-40 bg-slate-900 border-b border-slate-800 px-8 py-4 flex items-center justify-between">
            {!sidebarOpen && (
              <button 
                onClick={() => setSidebarOpen(true)}
                className="text-slate-400 hover:text-white"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}
            <div className="flex items-center gap-4">
              <div className="text-slate-400 text-sm">
                {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              {isRoot(currentUser) && (
                <div>
                  {(() => {
                    const options = (companies || []).filter(c => !(currentView === 'users-external' && String(c.name).toLowerCase().trim() === INTERNAL_COMPANY.NAME.toLowerCase()));
                    return (
                      <select className="bg-slate-800 text-slate-300 p-2 rounded" value={selectedCompanyId ?? ''} onChange={e => setSelectedCompanyId(e.target.value ? Number(e.target.value) : undefined)}>
                        <option value="">Todas las empresas</option>
                        {options.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main content area */}
        <div className={currentView === 'chat-analitico' ? 'flex-1 min-h-0' : 'overflow-auto flex-1'}>
          {currentView === 'dashboards' && (
            <DashboardsView
              mockDashboards={dashboards}
              dashboardsLoading={dashboardsLoading || dashboardsFetching}
              dashboardsError={dashboardsError}
              currentUser={currentUser}
              setSelectedDashboard={setSelectedDashboard}
              setCurrentView={setCurrentView}
              selectedCompanyId={selectedCompanyId}
              companyName={companies.find(c => c.id === selectedCompanyId)?.name}
              companies={companies}
              addDashboard={async (createdDashboard: any) => {
                try {
                  console.log('📝 Adding already created dashboard to list:', createdDashboard);
                  
                  queryClient.setQueryData(queryKeys.dashboards, (prev: any[] = []) => {
                    const newList = [createdDashboard, ...prev];
                    console.log('🔄 Dashboards updated: from', prev.length, 'to', newList.length);
                    return newList;
                  });
                  
                  // Show success message
                  toast.success(`Dashboard "${createdDashboard.name}" agregado a la lista`);
                  
                  // Registrar actividad de creación
                  logActivity('Dashboards', 'create', createdDashboard.name, createdDashboard.id);
                  
                } catch (err: any) {
                  console.error('❌ Error adding dashboard to list:', err);
                  toast.error('Error agregando dashboard a la lista');
                }
              }}
              updateDashboard={async (id: string, d: any) => {
                try {
                  await updateDashboardMutation.mutateAsync({ id, payload: d });
                } catch (err: any) {
                  toast.error(err?.response?.data?.message || 'Error actualizando dashboard');
                }
              }}
              deleteDashboard={async (id: string) => {
                try {
                  // Obtener nombre antes de eliminar
                  const dashboardToDelete = dashboardsQuery.data?.find((d: any) => d.id === id);
                  const dashboardName = dashboardToDelete?.name || 'Dashboard';
                  
                  await deleteDashboardMutation.mutateAsync(id);
                  
                  // Registrar actividad de eliminación
                  logActivity('Dashboards', 'delete', dashboardName, id);
                } catch (err: any) {
                  toast.error(err?.response?.data?.message || 'Error eliminando dashboard');
                }
              }}
            />
          )}
          {currentView === 'admin-overview' && (
            <AdminOverview 
              currentUser={currentUser} 
              selectedCompanyId={selectedCompanyId} 
              companiesProp={companies}
              setCurrentView={setCurrentView}
            />
          )}
          {currentView === 'client-dashboard' && (
            <ClientDashboard currentUser={currentUser} />
          )}
          {currentView === 'activity' && (
            <ActivityView currentUser={currentUser} />
          )}
          {currentView === 'data-model' && (
            <DataModelView 
              datasourceId={selectedDatasourceId}
              onCreateDashboard={(datasourceIds) => {
                // Store datasource IDs for dashboard form
                setSelectedDatasourceId(datasourceIds[0]); // Use first as reference
                setCurrentView('dashboards');
                // Trigger dashboard creation modal
                // The dashboard form will load models from these datasources
              }}
            />
          )}
          {currentView === 'data-modeling' && (
            <DataModeling />
          )}
          {currentView === 'chat-analitico' && (
            <ChatAnalitico />
          )}
          {currentView === 'users-internal' && (
            <UsersView
              mockUsers={users}
              currentUser={currentUser}
              addUser={async (u: any) => {
                try {
                  const payload = { ...u };
                  
                  // USUARIOS INTERNOS: Asegurar que tengan userType='internal' 
                  payload.userType = 'internal';
                  
                  // payload.companyId ya viene del formulario UserFormInterno
                  // Usar companyId como empresaId para el backend
                  if (payload.companyId) {
                    payload.empresaId = payload.companyId;
                  }
                  
                  // NO setear externalCompanyName para usuarios internos
                  delete payload.externalCompanyName;
                  
                  console.log('✅ Creating INTERNAL user with payload:', payload);
                  const created = await createUserMutation.mutateAsync(payload);
                  toast.success('Usuario interno creado exitosamente');
                } catch (err: any) {
                  console.error('❌ Error creating internal user:', err);
                  const errorMsg = err?.response?.data?.message || err?.message || 'Error creando usuario';
                  toast.error(errorMsg);
                }
              }}
              updateUser={async (id: string | number, u: any) => {
                try {
                  await updateUserMutation.mutateAsync({ id, payload: u });
                  toast.success('Usuario actualizado exitosamente');
                } catch (err: any) {
                  console.error('Error updating internal user:', err);
                  const errorMsg = err?.response?.data?.message || err?.message || 'Error actualizando usuario';
                  toast.error(errorMsg);
                }
              }}
              deleteUser={async (id: string | number) => {
                try {
                  await deleteUserMutation.mutateAsync(id);
                  toast.success('Usuario eliminado exitosamente');
                } catch (err) {
                  console.error('Error deleting user', err);
                  // show server message if present
                  // @ts-ignore
                  const msg = err?.response?.data?.message || err?.message || 'Error eliminando usuario';
                  toast.error(msg);
                }
              }}
              selectedCompanyId={selectedCompanyId}
              companyName={companies.find(c => c.id === selectedCompanyId)?.name}
              initialTab='internal'
              companies={companies}
            />
          )}

          {currentView === 'users-external' && (
            <UsersView
              mockUsers={users}
              currentUser={currentUser}
              addUser={async (u: any) => {
                try {
                  const payload = { ...u };
                  
                  // USUARIOS EXTERNOS: Asegurar que tengan userType='external'
                  payload.userType = 'external';
                  
                  // Si viene externalCompanyName, buscar la empresa cliente
                  if (payload.externalCompanyName && !payload.empresaId) {
                    const matchingCompany = companies.find((c) => 
                      c.name?.toLowerCase().trim() === payload.externalCompanyName.toLowerCase().trim()
                    );
                    
                    if (matchingCompany) {
                      payload.empresaId = matchingCompany.id;
                    } else {
                      toast.error(`No se encontró la empresa cliente: ${payload.externalCompanyName}`);
                      return;
                    }
                  }
                  
                  // NO setear companyId (es solo para usuarios internos)
                  delete payload.companyId;
                  
                  console.log('✅ Creating EXTERNAL user with payload:', payload);
                  await createUserMutation.mutateAsync(payload);
                  toast.success('Usuario externo creado exitosamente');
                } catch (err: any) {
                  console.error('Error creating external user:', err);
                  const errorMsg = err?.response?.data?.message || err?.message || 'Error creando usuario';
                  toast.error(errorMsg);
                }
              }}
              updateUser={async (id: string | number, u: any) => {
                try {
                  await updateUserMutation.mutateAsync({ id, payload: u });
                  toast.success('Usuario actualizado exitosamente');
                } catch (err: any) {
                  console.error('Error updating external user:', err);
                  const errorMsg = err?.response?.data?.message || err?.message || 'Error actualizando usuario';
                  toast.error(errorMsg);
                }
              }}
              deleteUser={async (id: string | number) => {
                try {
                  await deleteUserMutation.mutateAsync(id);
                  toast.success('Usuario eliminado exitosamente');
                } catch (err) {
                  console.error('Error deleting user', err);
                  // show server message if present
                  // @ts-ignore
                  const msg = err?.response?.data?.message || err?.message || 'Error eliminando usuario';
                  toast.error(msg);
                }
              }}
              selectedCompanyId={selectedCompanyId}
              companyName={companies.find(c => c.id === selectedCompanyId)?.name}
              initialTab='external'
              companies={companies}
            />
          )}
          {currentView === 'datasources' && (
            <DataSourcesView
              mockDataSources={dataSources}
              datasourcesLoading={datasourcesLoading || datasourcesFetching}
              datasourcesError={datasourcesError}
              currentUser={currentUser}
              selectedCompanyId={selectedCompanyId}
              companyName={companies.find(c => c.id === selectedCompanyId)?.name}
              companies={companies}
              setSelectedCompanyId={setSelectedCompanyId}
              onReload={async () => {
                await queryClient.invalidateQueries({ queryKey: queryKeys.datasources });
              }}
              onNavigateToModel={(datasourceId) => {
                setSelectedDatasourceId(datasourceId);
                setCurrentView('data-model');
              }}
              addDataSource={async (d: any) => {
                try {
                  setGlobalLoading(true);
                  setGlobalLoadingText('Creando y analizando fuente de datos...\nEsto puede tardar unos segundos.');
                  const payload = { ...d, status: d.status || 'active' };
                  const created = await createDataSourceMutation.mutateAsync(payload);
                  
                  // ✅ NO MANUAL ANALYSIS - Backend handles it automatically
                  console.log(`✅ DataSource created with auto-analysis: ${created.id}`);
                  
                  // Registrar actividad de creación
                  logActivity('Fuentes de Datos', 'create', created.name || d.name || 'Fuente de Datos', created.id);
                  
                  setGlobalLoading(false);
                  toast.success('Fuente de datos creada y analizada correctamente');
                } catch (err) {
                  setGlobalLoading(false);
                  toast.error('Error creando fuente de datos');
                  console.error('DataSource creation error:', err);
                }
              }}
              updateDataSource={async (id: string | number, d: any) => {
                try {
                  await updateDataSourceMutation.mutateAsync({ id, payload: d });
                  await queryClient.invalidateQueries({ queryKey: queryKeys.datasources });
                } catch (err) {
                  toast.error('Error actualizando fuente de datos');
                }
              }}
              deleteDataSource={async (id: string | number) => {
                try {
                  // Obtener nombre antes de eliminar
                  const datasourceToDelete = datasourcesQuery.data?.find((ds: any) => ds.id === id);
                  const datasourceName = datasourceToDelete?.name || 'Fuente de Datos';
                  
                  await deleteDataSourceMutation.mutateAsync(id);
                  
                  // Registrar actividad de eliminación
                  logActivity('Fuentes de Datos', 'delete', datasourceName, id);
                } catch (err: any) {
                  const errorMsg = err.response?.data?.message || err.message || 'Error eliminando fuente de datos';
                  console.error('Delete datasource error:', err);
                  toast.error(errorMsg);
                }
              }}
            />
          )}
          {currentView === 'credentials' && (
            <CredentialsPage />
          )}
          {currentView === 'companies' && (
            <CompaniesView
              companies={companies}
              addCompany={async (c: any) => {
                try {
                  await createCompanyMutation.mutateAsync(c);
                } catch (err) {
                  console.error('Error creating company:', err);
                  toast.error('Error creando empresa');
                }
              }}
              updateCompany={async (id: string | number, c: any) => {
                try {
                  await updateCompanyMutation.mutateAsync({ id, payload: c });
                } catch (err) {
                  console.error('Error updating company:', err);
                  toast.error('Error actualizando empresa');
                }
              }}
              deleteCompany={async (id: string | number) => {
                try {
                  await deleteCompanyMutation.mutateAsync(id);
                } catch (err) {
                  console.error('Error deleting company:', err);
                  toast.error('Error eliminando empresa');
                }
              }}
            />
          )}
          {currentView === 'viewer' && selectedDashboard && (
            <DashboardPage 
              dashboardId={selectedDashboard.id}
              onBack={() => setCurrentView('dashboards')}
            />
          )}
          {currentView === 'settings' && <SettingsView />}
          {currentView === 'dashboard-preview' && <DashboardPreview />}
          {currentView === 'executive-preview' && <ExecutivePreview />}
        </div>
      </div>
      
      <GlobalLoader visible={globalLoading} text={globalLoadingText} />
      
      {/* AION Assistant - Global */}
      {currentUser && <AionAssistant />}
    </div>
  );
};

export default App;
